import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import UserImg from "../assets/media/user_profile/user_img_01.jpg";
import ProfileAPI from "../api/ProfileAPI";
import toast from "react-hot-toast";
import UpdateProfileAPI from "../api/UpdateProfileAPI";

const Profile = () => {
  const { id } = useParams();
  const [passData, setPassData] = useState("my_profile");
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Saved profile data
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    phone: "",
    // address: "",
    image: "",
  });

  // Temp edit data
  const [editProfile, setEditProfile] = useState({
    name: "",
    phone: "",
    // address: "",
    email: "",
    image: "",
  });

  const [imagePreview, setImagePreview] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  // Get user ID from localStorage if not in params
  const userId = useMemo(() => {
    if (id) return id;

    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        return parsedUser?.id;
      }
    } catch (error) {
      console.error("Error parsing user from localStorage:", error);
    }
    return null;
  }, [id]);

  // Optimize image URL generation
  const generateImageUrl = useCallback((imagePath) => {
    if (!imagePath || imagePath === "") return UserImg;

    if (imagePath.startsWith("http") || imagePath.startsWith("blob:")) {
      return imagePath;
    }

    // Use environment variable or fallback to localhost
    const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
    return `${baseUrl}${imagePath}`;
  }, []);

  // Optimized profile data fetcher
  const fetchProfileData = useCallback(async () => {
    if (!userId || loading) return;

    try {
      setLoading(true);

      const response = await ProfileAPI.postProfile(userId);

      if (response.code === 200 || response.status === 200) {
        const userData = response.data;
        setProfileData(response.data);
        console.log(response.data);

        const profileData = {
          name: userData.name || "",
          email: userData.email || "",
          phone: userData.phone || "",
          // address: userData.address || "",
          image: userData.image || "",
        };

        setProfile(profileData);
        setImagePreview(generateImageUrl(profileData.image));

        // Only set edit profile if not currently editing to prevent data loss
        if (!isEditing) {
          setEditProfile({
            name: profileData.name,
            phone: profileData.phone,
            // address: profileData.address,
            email: profileData.email,
            image: profileData.image,
          });
        }
      } else {
        toast.error("Failed to load profile data");
      }
    } catch (error) {
      console.error("Profile fetch error:", error);
      toast.error("Error loading profile");
    } finally {
      setLoading(false);
      setHasInitialized(true);
    }
  }, [userId, loading, isEditing, generateImageUrl]);

  // Effect with proper dependencies
  useEffect(() => {
    if (userId && !hasInitialized) {
      fetchProfileData();
    }
  }, [userId, hasInitialized, fetchProfileData]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, []);

  const handleEditInputChange = useCallback((field, value) => {
    setEditProfile((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleImageChange = useCallback(
    (e) => {
      const file = e.target.files[0];

      if (!file) return;

      // Validate file type
      const validTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!validTypes.includes(file.type)) {
        toast.error("Please select a valid image file (JPEG, PNG, GIF, WebP)");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      // Cleanup previous blob URL
      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }

      // Create new preview
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);

      setEditProfile((prev) => ({
        ...prev,
        image: file,
      }));
    },
    [imagePreview]
  );

  const handleSaveAll = useCallback(async () => {
    if (!userId) {
      toast.error("User ID not found");
      return;
    }

    // Validate required fields
    if (!editProfile.name.trim()) {
      toast.error("Name is required");
      return;
    }

    try {
      setUpdateLoading(true);

      const formData = new FormData();
      formData.append("user_id", userId);
      formData.append("name", editProfile.name.trim());
      formData.append("email", editProfile.email.trim());
      formData.append("phone", editProfile.phone.trim());
      // formData.append("address", editProfile.address.trim());

      // Handle image upload
      if (editProfile.image instanceof File) {
        formData.append("image", editProfile.image);
      } else if (editProfile.image && typeof editProfile.image === "string") {
        formData.append("existing_image", editProfile.image);
      }

      const response = await UpdateProfileAPI.postProfileUpdate(formData);

      if (response.code === 200 || response.status === 200) {
        toast.success("Profile updated successfully!");

        // Refetch profile data
        setHasInitialized(false); // Allow refetch
        await fetchProfileData();
        setIsEditing(false);

        // Cleanup blob URL if it was a file upload
        if (
          editProfile.image instanceof File &&
          imagePreview.startsWith("blob:")
        ) {
          URL.revokeObjectURL(imagePreview);
        }
      } else {
        toast.error(response.message || "Failed to update profile");
      }
    } catch (error) {
      console.error("Update error:", error);

      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.status) {
        toast.error(`Server error: ${error.response.status}`);
      } else if (error.request) {
        toast.error("No response from server. Please check your connection.");
      } else {
        toast.error("Error updating profile");
      }
    } finally {
      setUpdateLoading(false);
    }
  }, [userId, editProfile, imagePreview, fetchProfileData]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);

    // Reset to original data
    setEditProfile({
      name: profile.name,
      phone: profile.phone,
      // address: profile.address,
      email: profile.email,
      image: profile.image,
    });

    // Reset image preview and cleanup blob URL
    if (imagePreview && imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }

    setImagePreview(generateImageUrl(profile.image));
  }, [profile, imagePreview, generateImageUrl]);

  const handleImageError = useCallback((e) => {
    console.error("Image load error:", e.target.src);
    e.target.src = UserImg;
  }, []);

  // Memoized profile form component
  const ProfileForm = useMemo(() => {
    if (loading) {
      return (
        <div className="container-fluid py-3">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "300px" }}
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="container-fluid py-3">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4>My Profile</h4>
              {isEditing ? (
                <div>
                  <button
                    className="btn btn-secondary me-2"
                    onClick={handleCancel}
                    disabled={updateLoading}
                  >
                    Cancel
                  </button>
                  <button
                    className="custom-profil-save-btn"
                    onClick={handleSaveAll}
                    disabled={updateLoading}
                  >
                    {updateLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </div>
              ) : (
                <button
                  className="custom-profil-edit-btn"
                  onClick={() => setIsEditing(true)}
                >
                  <i className="bi bi-pencil me-2"></i>
                  Edit Profile
                </button>
              )}
            </div>
            <hr />

            <div className="row gy-3">
              {/* Name */}
              <div className="col-md-6">
                <strong>Name:</strong>
                {isEditing ? (
                  <input
                    type="text"
                    className="form-control mt-1"
                    value={editProfile.name}
                    onChange={(e) =>
                      handleEditInputChange("name", e.target.value)
                    }
                    placeholder="Enter your name"
                    required
                  />
                ) : (
                  <span> {profile.name || "Not provided"}</span>
                )}
              </div>

              {/* Email (not editable) */}
              <div className="col-md-6">
                <strong>Email:</strong>
                {isEditing ? (
                  <input
                    type="email"
                    className="form-control mt-1"
                    value={profile.email}
                    readOnly
                    style={{ backgroundColor: "#f8f9fa" }}
                  />
                ) : (
                  <span> {profile.email || "Not provided"}</span>
                )}
              </div>

              {/* Phone */}
              <div className="col-md-6">
                <strong>Phone:</strong>
                {isEditing ? (
                  <input
                    type="text"
                    className="form-control mt-1"
                    value={editProfile.phone}
                    onChange={(e) =>
                      handleEditInputChange("phone", e.target.value)
                    }
                    placeholder="Enter phone number"
                  />
                ) : (
                  <span> {profile.phone || "Not provided"}</span>
                )}
              </div>

              {/* Address */}
              {/* <div className="col-md-6">
                <strong>Address:</strong>
                {isEditing ? (
                  <input
                    type="text"
                    className="form-control mt-1"
                    value={editProfile.address}
                    onChange={(e) => handleEditInputChange("address", e.target.value)}
                    placeholder="Enter address"
                  />
                ) : (
                  <span> {profile.address || "Not provided"}</span>
                )}
              </div> */}
            </div>
          </div>
        </div>
      </div>
    );
  }, [
    loading,
    isEditing,
    updateLoading,
    profile,
    editProfile,
    handleCancel,
    handleSaveAll,
    handleEditInputChange,
  ]);

  // Render data based on selected tab
  const renderData = useCallback(
    (data) => {
      switch (data) {
        case "my_profile":
          return ProfileForm;

        case "my_bookings":
          return (
            <div className="container-fluid py-3">
              <div className="row">
                <div className="col-12">
                  <h4>My Bookings</h4>
                  <hr />
                  <div className="table-responsive">
                    <table className="table table-bordered table-striped">
                      <thead className="table-light">
                        <tr>
                          <th>S.No</th>
                          <th>Destination</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profileData.bookings.map((data, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>{data.booking_for}</td>
                            <td>{data.booking_date}</td>
                            <td>
                              <span className="badge bg-success">
                                {data.payment_status}
                              </span>
                            </td>
                            <td>{data.total_amount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          );

        default:
          return (
            <div className="p-3">Please select an option from the menu.</div>
          );
      }
    },
    [ProfileForm]
  );

  // Don't render anything if no user ID
  if (!userId) {
    return (
      <section className="custom-profile-section">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="alert alert-warning" role="alert">
                User not found. Please log in again.
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="custom-profile-section">
      <div className="container-fluid">
        <div className="row gap-2 gap-lg-0">
          {/* Sidebar */}
          <div className="col-12 col-lg-3 px-0">
            <div className="custom-profile-left-box">
              <div className="container-fluid">
                <div className="row">
                  <div className="col-lg-12 d-flex flex-column justify-content-center align-items-center">
                    <div className="custom-profile-img">
                      <img
                        src={imagePreview || UserImg}
                        alt="Profile"
                        className="img-fluid rounded-circle"
                        style={{
                          width: "150px",
                          height: "150px",
                          objectFit: "cover",
                        }}
                        onError={handleImageError}
                      />
                      {isEditing && (
                        <div className="mt-2">
                          <input
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={handleImageChange}
                          />
                          <small className="text-muted">
                            Select new profile picture (Max: 5MB)
                          </small>
                        </div>
                      )}
                    </div>
                    <div className="custom-profile-user-name pb-2">
                      <p className="text-center mb-0">
                        {loading && !hasInitialized
                          ? "Loading..."
                          : profile.name || "User"}
                      </p>
                      <a href="#" className="text-center">
                        {loading && !hasInitialized
                          ? "Loading..."
                          : profile.email || "No email provided"}
                      </a>
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="custom-profile-buttons">
                      <div
                        className={`custom-profile-button-options ${
                          passData === "my_profile" ? "active" : ""
                        }`}
                        onClick={() => setPassData("my_profile")}
                      >
                        My Profile
                      </div>
                      <div
                        className={`custom-profile-button-options ${
                          passData === "my_bookings" ? "active" : ""
                        }`}
                        onClick={() => setPassData("my_bookings")}
                      >
                        My Bookings
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="col-12 col-lg-9 px-0">
            <div className="custom-profile-right-box">
              <div className="custom-profile-user-button-content">
                {renderData(passData)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Profile;
